import { Loader2 } from 'lucide-react';
import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerProfile } from '../../hooks/usePartnerSettings';
import type { Partner } from '../../lib/api';
import { SETTINGS_STRINGS } from '../../lib/constants';

interface ThemeStyles {
  background?: string;
  foreground?: string;
  card?: string;
  'card-foreground'?: string;
  popover?: string;
  'popover-foreground'?: string;
  primary?: string;
  'primary-foreground'?: string;
  secondary?: string;
  'secondary-foreground'?: string;
  muted?: string;
  'muted-foreground'?: string;
  accent?: string;
  'accent-foreground'?: string;
  destructive?: string;
  'destructive-foreground'?: string;
  border?: string;
  input?: string;
  ring?: string;
  'chart-1'?: string;
  'chart-2'?: string;
  'chart-3'?: string;
  'chart-4'?: string;
  'chart-5'?: string;
  radius?: string;
  'font-body'?: string;
  'font-heading'?: string;
  [key: string]: string | undefined;
}

interface ThemeConfig {
  styles?: ThemeStyles;
  darkMode?: boolean;
}

const NATIVE_GENERIC_FAMILIES = new Set(['system-ui', 'sans-serif', 'serif', 'monospace', 'cursive']);

/** First font in a CSS stack when it is not a generic keyword (candidate for Google Fonts). */
function getFirstFontFamily(fullFontText: string | undefined): string | undefined {
  if (!fullFontText) return undefined;
  const first = fullFontText.split(',')[0].trim();
  if (!first || NATIVE_GENERIC_FAMILIES.has(first)) return undefined;
  return first;
}

function buildGoogleFontUrl(families: string[]): string {
  const url = new URL('https://fonts.googleapis.com/css2');
  for (const f of families) {
    url.searchParams.append('family', `${f}:wght@400;500;600;700`);
  }
  url.searchParams.set('display', 'swap');
  return url.toString();
}

/** Single source of truth: theme token → default (HSL components, rem string, or empty for optional fonts). */
const THEME_STYLE_DEFAULTS = {
  background: '0 0% 100%',
  foreground: '225 15% 16%',
  card: '0 0% 100%',
  'card-foreground': '225 15% 16%',
  popover: '0 0% 100%',
  'popover-foreground': '225 15% 16%',
  primary: '210 30% 25%',
  'primary-foreground': '0 0% 100%',
  secondary: '210 15% 94%',
  'secondary-foreground': '210 30% 25%',
  muted: '210 10% 96%',
  'muted-foreground': '220 10% 45%',
  accent: '210 15% 94%',
  'accent-foreground': '210 30% 25%',
  destructive: '0 84.2% 60.2%',
  'destructive-foreground': '0 0% 100%',
  border: '220 13% 90%',
  input: '220 13% 90%',
  ring: '210 30% 25%',
  'chart-1': '210 50% 50%',
  'chart-2': '260 50% 55%',
  'chart-3': '150 50% 45%',
  'chart-4': '325 50% 55%',
  'chart-5': '45 80% 55%',
  radius: '0.625rem',
  'font-body': '',
  'font-heading': '',
} as const;

type ThemeStyleToken = keyof typeof THEME_STYLE_DEFAULTS;

const COLOR_STYLE_FIELDS: { key: ThemeStyleToken; label: string }[] = [
  { key: 'background', label: 'Background' },
  { key: 'foreground', label: 'Foreground' },
  { key: 'card', label: 'Card' },
  { key: 'card-foreground', label: 'Card Foreground' },
  { key: 'popover', label: 'Popover' },
  { key: 'popover-foreground', label: 'Popover Foreground' },
  { key: 'primary', label: 'Primary' },
  { key: 'primary-foreground', label: 'Primary Foreground' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'secondary-foreground', label: 'Secondary Foreground' },
  { key: 'muted', label: 'Muted' },
  { key: 'muted-foreground', label: 'Muted Foreground' },
  { key: 'accent', label: 'Accent' },
  { key: 'accent-foreground', label: 'Accent Foreground' },
  { key: 'destructive', label: 'Destructive' },
  { key: 'destructive-foreground', label: 'Destructive Foreground' },
  { key: 'border', label: 'Border' },
  { key: 'input', label: 'Input' },
  { key: 'ring', label: 'Ring' },
];

const CHART_COLOR_FIELDS: { key: ThemeStyleToken; label: string }[] = [
  { key: 'chart-1', label: 'Chart 1' },
  { key: 'chart-2', label: 'Chart 2' },
  { key: 'chart-3', label: 'Chart 3' },
  { key: 'chart-4', label: 'Chart 4' },
  { key: 'chart-5', label: 'Chart 5' },
];

const PREVIEW_FONT_BODY_FALLBACK = 'Inter, system-ui, sans-serif';
const PREVIEW_FONT_HEADING_FALLBACK = 'Plus Jakarta Sans, system-ui, sans-serif';

function resolveThemeToken(styles: ThemeStyles | undefined, key: ThemeStyleToken): string {
  const raw = styles?.[key];
  if (raw !== undefined && raw !== '') return raw;
  return THEME_STYLE_DEFAULTS[key];
}

/**
 * Theme Configuration Tab Component
 * Allows partners to customize their SDK theme
 */
export const ThemeTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [formData, setFormData] = React.useState<Partial<Partner>>({});

  React.useEffect(() => {
    if (partner) {
      setFormData({
        themeConfig: partner.themeConfig,
      });
    }
  }, [partner]);

  // Dynamically load Google Fonts for live preview (`styles` is typed on ThemeConfig; avoid double-casting)
  const themeStyles = (formData.themeConfig as ThemeConfig)?.styles;
  const token = (key: ThemeStyleToken) => resolveThemeToken(themeStyles, key);
  const bodyFontStack = token('font-body');
  const headingFontStack = token('font-heading');

  React.useEffect(() => {
    const fonts = [bodyFontStack, headingFontStack]
      .map(getFirstFontFamily)
      .filter((f): f is string => Boolean(f));

    const uniqueFonts = [...new Set(fonts)];
    if (uniqueFonts.length === 0) return;

    const linkId = 'theme-preview-fonts';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    const href = buildGoogleFontUrl(uniqueFonts);

    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;

    return () => {
      link?.remove();
    };
  }, [bodyFontStack, headingFontStack]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({ themeConfig: formData.themeConfig });
  };

  const handleThemeStyleChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      themeConfig: {
        ...((prev.themeConfig as ThemeConfig) || {}),
        styles: {
          ...((prev.themeConfig as ThemeConfig)?.styles || {}),
          [key]: value,
        },
      },
    }));
  };

  const isHex = (value: string): boolean => /^#([a-f\d]{3}|[a-f\d]{6})$/i.test(value);

  // Return a valid CSS color string whether the input is HSL or hex
  const toCssColor = (value: string): string => {
    if (!value) return '#000000';
    if (isHex(value)) return value;
    return `hsl(${value})`;
  };

  // Convert HSL to hex for color picker
  const hslToHex = (hsl: string): string => {
    if (!hsl) return '#000000';
    if (isHex(hsl)) return hsl;
    const match = hsl.match(/(\d+\.?\d*)\s+(\d+\.?\d*)%\s+(\d+\.?\d*)%/);
    if (!match) return '#000000';

    const h = parseFloat(match[1]);
    const s = parseFloat(match[2]) / 100;
    const l = parseFloat(match[3]) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;
    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    const toHex = (n: number) => {
      const hex = Math.round((n + m) * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const strings = SETTINGS_STRINGS.THEME_CONFIGURATION;

  const radiusNumeric = parseFloat(token('radius')) || 0.625;
  const radiusSliderFillPercent = (radiusNumeric / 2) * 100;
  const radiusSliderGradient = `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${radiusSliderFillPercent}%, #e5e7eb ${radiusSliderFillPercent}%, #e5e7eb 100%)`;

  return (
    <div className="space-y-6">
      {/* Theme Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Color Styles */}
              <div>
                <h4 className="text-sm font-medium mb-2">{strings.COLOR_STYLES_LABEL}</h4>
                <div className="grid grid-cols-1 gap-2">
                  {COLOR_STYLE_FIELDS.map(field => {
                    const hslValue = token(field.key);
                    const hexValue = hslValue ? hslToHex(hslValue) : '#000000';

                    return (
                      <div key={field.key} className="flex items-center gap-2">
                        <Label htmlFor={`theme-${field.key}`} className="text-xs w-24 flex-shrink-0">
                          {field.label}
                        </Label>
                        <label className="relative w-8 h-8 rounded border border-input cursor-pointer overflow-hidden flex-shrink-0">
                          <div className="absolute inset-0" style={{ backgroundColor: hexValue }} />
                          <input
                            type="color"
                            value={hexValue}
                            onChange={e => handleThemeStyleChange(field.key, e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            title="Pick a color"
                          />
                        </label>
                        <Input
                          id={`theme-${field.key}`}
                          value={hslValue}
                          onChange={e => handleThemeStyleChange(field.key, e.target.value)}
                          placeholder={`e.g., ${THEME_STYLE_DEFAULTS[field.key]}`}
                          className="text-xs font-mono flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Chart Colors */}
              <div>
                <h4 className="text-sm font-medium mb-2">Chart Colors</h4>
                <div className="grid grid-cols-1 gap-2">
                  {CHART_COLOR_FIELDS.map(field => {
                    const hslValue = token(field.key);
                    const hexValue = hslValue ? hslToHex(hslValue) : '#000000';

                    return (
                      <div key={field.key} className="flex items-center gap-2">
                        <Label htmlFor={`theme-${field.key}`} className="text-xs w-24 flex-shrink-0">
                          {field.label}
                        </Label>
                        <label className="relative w-8 h-8 rounded border border-input cursor-pointer overflow-hidden flex-shrink-0">
                          <div className="absolute inset-0" style={{ backgroundColor: hexValue }} />
                          <input
                            type="color"
                            value={hexValue}
                            onChange={e => handleThemeStyleChange(field.key, e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            title="Pick a color"
                          />
                        </label>
                        <Input
                          id={`theme-${field.key}`}
                          value={hslValue}
                          onChange={e => handleThemeStyleChange(field.key, e.target.value)}
                          placeholder={`e.g., ${THEME_STYLE_DEFAULTS[field.key]}`}
                          className="text-xs font-mono flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Typography */}
              <div>
                <h4 className="text-sm font-medium mb-2">Typography</h4>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="font-body" className="text-xs">
                      Body Font
                    </Label>
                    <Input
                      id="font-body"
                      value={token('font-body')}
                      onChange={e => {
                        const customValue = e.target.value;
                        handleThemeStyleChange('font-body', customValue);
                      }}
                      placeholder="Inter, system-ui, sans-serif"
                      className="text-sm font-mono"
                      style={{
                        fontFamily: token('font-body') || PREVIEW_FONT_BODY_FALLBACK,
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="font-heading" className="text-xs">
                      Heading Font
                    </Label>
                    <Input
                      id="font-heading"
                      value={token('font-heading')}
                      onChange={e => {
                        const customValue = e.target.value;
                        handleThemeStyleChange('font-heading', customValue);
                      }}
                      placeholder="Plus Jakarta Sans, system-ui, sans-serif"
                      className="text-sm font-mono"
                      style={{
                        fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Border Radius */}
              <div>
                <h4 className="text-sm font-medium mb-2">{strings.BORDER_RADIUS_LABEL}</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-xs">Radius</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="2"
                        step="0.125"
                        value={radiusNumeric}
                        onChange={e => {
                          const val = e.target.value === '' ? 0 : Number(e.target.value);
                          handleThemeStyleChange('radius', `${Math.min(2, Math.max(0, val))}rem`);
                        }}
                        className="w-16 text-xs text-right"
                      />
                      <span className="text-xs font-medium">rem</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.125"
                    value={radiusNumeric}
                    onChange={e => handleThemeStyleChange('radius', `${e.target.value}rem`)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                    style={{ background: radiusSliderGradient }}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0rem (sharp)</span>
                    <span>2rem (very round)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (partner) {
                      setFormData({
                        themeConfig: partner.themeConfig,
                      });
                    }
                  }}
                  disabled={isUpdating}>
                  {SETTINGS_STRINGS.ORGANIZATION_PROFILE.CANCEL_BUTTON}
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    SETTINGS_STRINGS.ORGANIZATION_PROFILE.SAVE_BUTTON
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Live Preview Panel */}
        <Card className="sticky top-6 self-start">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
            <div
              className="border p-6 space-y-4"
              style={{
                backgroundColor: toCssColor(token('background')),
                color: toCssColor(token('foreground')),
                borderColor: toCssColor(token('border')),
                borderRadius: token('radius'),
                fontFamily: token('font-body') || PREVIEW_FONT_BODY_FALLBACK,
              }}>
              {/* Heading Example */}
              <h3
                className="text-xl font-bold"
                style={{
                  fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK,
                }}>
                Theme Preview
              </h3>

              {/* Card Example */}
              <div
                className="border p-4 space-y-2"
                style={{
                  backgroundColor: toCssColor(token('card')),
                  color: toCssColor(token('card-foreground')),
                  borderColor: toCssColor(token('border')),
                  borderRadius: token('radius'),
                }}>
                <h5
                  className="font-semibold"
                  style={{
                    fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK,
                  }}>
                  Card Title
                </h5>
                <p className="text-sm">This is an example card component</p>
              </div>

              {/* Popover Example */}
              <div
                className="inline-block border p-3 shadow-lg"
                style={{
                  backgroundColor: toCssColor(token('popover')),
                  color: toCssColor(token('popover-foreground')),
                  borderColor: toCssColor(token('border')),
                  borderRadius: token('radius'),
                }}>
                <p className="text-sm">Popover content</p>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: toCssColor(token('primary')),
                    color: toCssColor(token('primary-foreground')),
                    borderRadius: token('radius'),
                  }}>
                  Primary Button
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: toCssColor(token('secondary')),
                    color: toCssColor(token('secondary-foreground')),
                    borderRadius: token('radius'),
                  }}>
                  Secondary
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: toCssColor(token('destructive')),
                    color: toCssColor(token('destructive-foreground')),
                    borderRadius: token('radius'),
                  }}>
                  Destructive
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: toCssColor(token('accent')),
                    color: toCssColor(token('accent-foreground')),
                    borderRadius: token('radius'),
                  }}>
                  Accent
                </button>
              </div>

              {/* Muted Section */}
              <div
                className="p-3"
                style={{
                  backgroundColor: toCssColor(token('muted')),
                  color: toCssColor(token('muted-foreground')),
                  borderRadius: token('radius'),
                }}>
                <p className="text-sm">Muted section with muted text</p>
              </div>

              {/* Input Example */}
              <input
                type="text"
                placeholder="Input field"
                className="w-full px-3 py-2 text-sm focus:outline-none"
                style={{
                  backgroundColor: toCssColor(token('background')),
                  color: toCssColor(token('foreground')),
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: toCssColor(token('input')),
                  borderRadius: token('radius'),
                  outline: `2px solid transparent`,
                }}
                onFocus={e => {
                  e.target.style.outline = `2px solid ${toCssColor(token('ring'))}`;
                  e.target.style.outlineOffset = '2px';
                }}
                onBlur={e => {
                  e.target.style.outline = '2px solid transparent';
                }}
              />

              {/* Chart Colors Preview */}
              <div className="flex gap-2">
                {CHART_COLOR_FIELDS.map(field => (
                  <div
                    key={field.key}
                    className="flex-1 h-12 rounded"
                    style={{
                      backgroundColor: toCssColor(token(field.key)),
                      borderRadius: token('radius'),
                    }}
                    title={field.label}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
